import { useState } from 'react';

import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { getMonthInterval } from 'utils/date';

import { mutations, queries, queryKeys } from 'RestAPIs/rapini';

import { Box } from '@mui/material';

import { DataGrid, DateNavigation } from 'components/shared';
import { faCheck } from '@fortawesome/pro-light-svg-icons';
import { useQueryClient } from 'react-query';

const AbsenceNotices = () => {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const { startDate } = getMonthInterval(new Date());
  const [selectedDate, setSelectedDate] = useState(startDate);
  const monthInterval = getMonthInterval(selectedDate);
  const { data: absenceNotices, isLoading } = queries.useGetAbsenceNotices(
    dayjs(monthInterval.startDate).format('YYYY-MM-DD'),
    dayjs(monthInterval.endDate).format('YYYY-MM-DD'),
    undefined
  );
  const { mutate: processAbsenceNotice } = mutations.useProcessAbsenceNotice();

  const onClickProcess = (id: string) => {
    processAbsenceNotice({
      absenceNoticeId: id
    });
    queryClient.invalidateQueries(queryKeys.getAbsenceNotices.name);
  };

  const columns = [
    { field: 'employeeNumber', headerName: t('employeeNr'), minWidth: 40 },
    { field: 'employee', headerName: t('employee'), minWidth: 250 },
    { field: 'start', headerName: t('startTime'), minWidth: 120 },
    { field: 'end', headerName: t('endTime'), minWidth: 120 },
    { field: 'status', headerName: t('status'), minWidth: 40 },
    { field: 'createdOn', headerName: t('createdOn'), minWidth: 120 },
    { field: 'reason', headerName: t('reason'), flex: 1 }
  ];

  const rows =
    absenceNotices
      ?.filter((d) => !dayjs(d.start).isBefore(dayjs(monthInterval.startDate)))
      ?.sort(
        (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
      )
      .map((d) => ({
        id: d.entityId,
        employeeNumber: d.employee?.number,
        employee: d.employee?.nameInformal,
        status: t(`absenceNoticeStatus.${d.status}`),
        start: dayjs(d.start).format('DD-MM-YYYY HH:mm'),
        end: dayjs(d.end).format('DD-MM-YYYY HH:mm'),
        createdOn: dayjs(d.createdOn).format('DD-MM-YYYY HH:mm'),
        reason: d.reason
      })) ?? [];
  return (
    <Box p={2}>
      <Box py={2}>
        <DateNavigation
          defaultPeriod="month"
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
        />
      </Box>
      <DataGrid
        columns={columns}
        rows={rows}
        loading={isLoading}
        customActions={(id, row) => [
          ...(row?.status && row.status == t('absenceNoticeStatus.RECEIVED')
            ? [
                {
                  tooltipTitle: t('process'),
                  icon: faCheck,
                  onClick: () => onClickProcess(id as string)
                }
              ]
            : [])
        ]}
      />
    </Box>
  );
};

export default AbsenceNotices;
